import { Link, useRouterState } from "@tanstack/react-router";
import { cn } from "@workspace/web-ui/lib/utils";
import { useState } from "react";
import { HiMenuAlt3 } from "react-icons/hi";
import { LogoBlack } from "~/components/logo";
import { NavigationItem, urgentCareNavigationItems, weightLossNavigationItems } from "~/features/dashboard/data/navigation";
import { Sheet, SheetContent } from "@workspace/web-ui/components/sheet";
// import { Button } from "@workspace/web-ui/components/button";
import { ChevronDown, ChevronsUpDown } from "lucide-react";
import { useTaskStore } from "../store/task";
import { AlfieChatBot } from "./alfie-chat";

export function DashboardSidebar() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [menuOpen, setMenuOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showChatBot, setShowChatBot] = useState(false);
  const {taskCount} = useTaskStore()
  
  // Determine current platform
  const isWeightLossPlatform = !pathname.startsWith("/dashboard/urgent-care");
  const currentItems = isWeightLossPlatform ? weightLossNavigationItems : urgentCareNavigationItems;
  const currentTitle = isWeightLossPlatform ? "Weight Loss" : "Urgent Care";
  const currentInitials = isWeightLossPlatform ? "WL" : "UC";

  function isRouteActive(item: NavigationItem, pathname: string) {
    return (
      item.url === pathname ||
      (item.matchUrls?.length &&
        item.matchUrls.some((matchUrl: string) =>
          pathname.startsWith(matchUrl)
        ))
    );
  }

  const handlePlatformSwitch = (targetPlatform: 'weight-loss' | 'urgent-care') => {
    // Only show chat bot when switching FROM weight loss TO urgent care
    if (isWeightLossPlatform && targetPlatform === 'urgent-care') {
      setShowChatBot(true);
      // Don't navigate yet - let the chatbot handle the navigation
      setIsOpen(false);
      setMenuOpen(false);
      return;
    }
    setIsOpen(false);
    setMenuOpen(false);
  };

  return (
    <>
      {/* Mobile Header */}
      <header className="sticky top-0 z-10 w-full bg-[#0A1A2F] px-4 py-4 sm:hidden">
        <div className="flex items-center justify-between w-full">
          <Link to="/dashboard" className="flex items-center gap-2">
            <LogoBlack className="h-8 w-auto" />
          </Link>
          <button
            className="rounded-full border border-[#334155] p-2"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <HiMenuAlt3 className="h-5 w-5 text-white" />
          </button>
        </div>
      </header>

      {/* Mobile Sheet Menu */}
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent
          side="left"
          className="w-full border-0 bg-[#0A1A2F] text-white sm:hidden"
        >
          <div className="flex h-full flex-col">
            <div className="pb-6">
              <Link
                to={isWeightLossPlatform ? "/dashboard/weight-loss" : "/dashboard/urgent-care"}
                className="flex items-center gap-2"
                onClick={() => setMenuOpen(false)}
              >
                <LogoBlack className="h-8 w-auto" />
              </Link>
            </div>
            <div className="flex flex-col gap-6">
              {currentItems.map((item) => {
                const isActive = isRouteActive(item, pathname);
                return (
                  <Link
                    key={item.url}
                    to={item.url}
                    onClick={() => setMenuOpen(false)}
                    className={cn(
                      "flex items-center gap-3 text-base",
                      isActive ? "text-white" : "text-[#667281]"
                    )}
                  >
                    {item.icon && (
                      <span className="text-lg">{item.icon}</span>
                    )}
                    <span>{item.title}</span>
                    {taskCount && item.title === "Home" && (
                      <span className="ml-2 flex h-5 w-5 items-center justify-center rounded-full bg-blue-500 text-xs font-medium text-white">
                        {taskCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          <div className="mt-auto">
            <div className="rounded-md p-2">
              <div className="text-sm text-[#667281] px-2 pb-2">
                Switch Platforms
              </div>
              <div className="relative">
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  className="flex w-full items-center border border-[#4272DD] justify-between rounded-md bg-[#1E293B] px-3 py-2"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-white">{currentTitle}</span>
                  </div>
                  <ChevronDown className="h-4 w-4 text-[#667281]" />
                </button>

                {isOpen && (
                  <div className="absolute bottom-full mb-2 w-full rounded-md bg-[#1E293B] p-2 shadow-lg">
                    <div className="flex flex-col gap-2">
                      <Link 
                        to="/dashboard"
                        onClick={() => handlePlatformSwitch('weight-loss')}
                        className={`flex w-full items-center justify-between rounded-md bg-transparent px-2 py-1.5 ${isWeightLossPlatform ? 'text-white' : 'text-[#667281] hover:bg-[#FFFFFF0D]'}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm">Weight Loss</span>
                        </div>
                        {isWeightLossPlatform && <div className="h-2 w-2 rounded-full bg-green-500"></div>}
                      </Link>
                      {/* <button
                        onClick={() => handlePlatformSwitch('urgent-care')}
                        className={`flex w-full items-center justify-between rounded-md bg-transparent px-2 py-1.5 ${!isWeightLossPlatform ? 'text-white' : 'text-[#667281] hover:bg-[#FFFFFF0D]'}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm">Urgent Care</span>
                        </div>
                        {!isWeightLossPlatform && <div className="h-2 w-2 rounded-full bg-green-500"></div>}
                      </button> */}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Desktop Sidebar */}
      <aside className="fixed left-0 top-0 hidden h-full w-[286px] flex-col bg-[#0A1A2F] sm:flex">
        <div className="flex h-full flex-col p-4">
          {/* Logo */}
          <div className="mb-8 flex items-center gap-2 px-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-[#FFFFFF1A] shadow-inner shadow-[#FFFFFF26] text-white">
              <span className="text-xs font-bold">{currentInitials}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-medium text-white">{currentTitle}</span>
              <span className="text-xs text-[#667281]">Maurice Umoh</span>
            </div>
            <ChevronsUpDown className="ml-auto h-6 w-6 text-[#667281]" />
          </div>

          {/* Navigation */}
          <nav className="flex flex-col">
            {currentItems.map((item) => {
              const isActive = isRouteActive(item, pathname);
              return (
                <Link
                  key={item.url}
                  to={item.url}
                  className={cn(
                    "flex items-center rounded-md px-3 py-2.5",
                    isActive
                      ? "bg-[#FFFFFF0D] border border-[#FFFFFF0D] text-white"
                      : "text-[#667281] hover:bg-[#1E293B]/50"
                  )}
                >
                  {item.icon && (
                    <span className="mr-3 text-lg">{item.icon}</span>
                  )}
                  <span className="text-sm">{item.title}</span>
                  {taskCount > 0 && item.title === "Home" && (
                    <span className="ml-2 flex h-5 w-5 items-center justify-center rounded-full bg-[#4272DD] shadow-inner shadow-[#FFFFFF40] text-xs font-medium text-white">
                      {taskCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

                  {/* Platform Switcher */}
          <div className="mt-auto">
            <div className="rounded-md p-2">
              <div className="text-sm text-[#667281] px-2 pb-2">
                Switch Platforms
              </div>
              <div className="relative">
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  className="flex w-full items-center border border-[#4272DD] justify-between rounded-md bg-[#1E293B] px-3 py-2"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-white">{currentTitle}</span>
                  </div>
                  <ChevronDown className="h-4 w-4 text-[#667281]" />
                </button>

                {isOpen && (
                  <div className="absolute bottom-full mb-2 w-full rounded-md bg-[#1E293B] p-2 shadow-lg">
                    <div className="flex flex-col gap-2">
                      <Link 
                        to="/dashboard"
                        onClick={() => handlePlatformSwitch('weight-loss')}
                        className={`flex w-full items-center justify-between rounded-md bg-transparent px-2 py-1.5 ${isWeightLossPlatform ? 'text-white' : 'text-[#667281] hover:bg-[#FFFFFF0D]'}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm">Weight Loss</span>
                        </div>
                        {isWeightLossPlatform && <div className="h-2 w-2 rounded-full bg-green-500"></div>}
                      </Link>
                      {/* <button
                        onClick={() => handlePlatformSwitch('urgent-care')}
                        className={`flex w-full items-center justify-between rounded-md bg-transparent px-2 py-1.5 ${!isWeightLossPlatform ? 'text-white' : 'text-[#667281] hover:bg-[#FFFFFF0D]'}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm">Urgent Care</span>
                        </div>
                        {!isWeightLossPlatform && <div className="h-2 w-2 rounded-full bg-green-500"></div>}
                      </button> */}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <AlfieChatBot open={showChatBot} onOpenChange={setShowChatBot} />
      </aside>
    </>
  );
}